/**
 * emailSource.ts
 *
 * Collects the API contract's EmailPayload from the current Office mail
 * item. Header fetch, HTML body fetch, and attachment fetch are
 * independent async operations and run concurrently (Promise.all) rather
 * than sequentially.
 *
 * `collectEmail` returns `CollectedEmail`, a strict superset of
 * `EmailPayload` (the plan's payload contract, mirrored 1:1 in
 * api/types.ts) that also carries `skippedAttachments` — attachments
 * attachmentQueue.ts could not fetch as bytes (cloud/URL attachments).
 * That list is client-side-only UI data ("not filed: cloud attachment");
 * callers must strip it before sending `email` to POST /filings.
 */
import type { SkippedAttachment } from "./attachmentQueue";
import { fetchAttachments } from "./attachmentQueue";
import type { MailItemLike } from "./officeShim";
import type { EmailAttachment, EmailPayload } from "../api/types";

export interface CollectedEmail extends EmailPayload {
  skippedAttachments: SkippedAttachment[];
}

export async function collectEmail(
  item: MailItemLike,
): Promise<CollectedEmail> {
  const [headersRaw, htmlBody, attachmentResult] = await Promise.all([
    item.getAllInternetHeadersAsync(),
    item.getHtmlBodyAsync(),
    fetchAttachments(item),
  ]);

  const attachments: EmailAttachment[] = attachmentResult.fetched.map(
    (fetched) => ({
      name: fetched.name,
      contentType: fetched.contentType,
      size: fetched.size,
      isInline: fetched.isInline,
      contentId: fetched.contentId,
      base64: fetched.base64,
    }),
  );

  return {
    internetMessageId: item.internetMessageId,
    subject: item.subject,
    sentDateTime: item.sentDateTime,
    from: item.from,
    to: item.to,
    cc: item.cc,
    headersRaw,
    htmlBody,
    attachments,
    skippedAttachments: attachmentResult.skipped,
  };
}

/** Strips the client-only skip list, leaving exactly the wire payload. */
export function toEmailPayload(collected: CollectedEmail): EmailPayload {
  const {
    internetMessageId,
    subject,
    sentDateTime,
    from,
    to,
    cc,
    headersRaw,
    htmlBody,
    attachments,
  } = collected;
  return {
    internetMessageId,
    subject,
    sentDateTime,
    from,
    to,
    cc,
    headersRaw,
    htmlBody,
    attachments,
  };
}
